import { useEffect, useState } from "react";
import BackButton from "../helpers/BackButton";
import {
  BusinessVerificationStage,
  getEquivalentBackendStage,
} from "../verificationModels";
import RedX from "assets/Icon_RedCross.png";
import GreenTick from "assets/Icon_GreenTick.png";
import { useDispatch, useSelector } from "react-redux";
import { endpoints } from "config";
import instance, { ApiResponse, useFetch } from "api";
import React from "react";
// import {
//     NotificationIdentifier,
//     completeUIUpdate,
//     selectUIUpdate,
// } from 'components/notifications/notificationUIUpdateReducer';
import ExitContinueButton, {
  formatValues,
} from "../helpers/ExitContinueButtons";
import StageIndicator from "../helpers/StageIndicator";
import { useField, useFormikContext } from "formik";
import { Spinner } from "components/Spinner/Spinner";
import { ErrorM } from "components/helpers/ErrorM";
import Button from "components/ui/Button";
import { ModalTypes, openModal } from "reducers/globalModal";
import { selectVerificationDetails } from "reducers/verification";
import { FormValues } from "../verificationSchema";
import { Toast, ToastMessageReason } from "components/helpers/toast";
interface Props {}

export type DocumentFieldValue = {
  documentTypesID: number;
  fileName: string | null;
  bAlwaysRequired: boolean;
};

type Errors = {
  documentId: number;
  errorMessage: string;
}[];

type Data = {
  documentId: number;
  fileName: string;
  documentStatus: number;
  documentStatusString: string;
  name: string;
  orderNumber: number;
  documentNotes?: string;
  uploadNotes: string;
  bAlwaysRequired: boolean;
  rejectionReason: string | null;
};

const statusesToUploadFrom: Data["documentStatusString"][] = [
  "Not Submitted",
  "Expired",
  "Rejected",
  "Checking",
];

const BusinessVerificationDocuments: React.FC<Props> = props => {
  const { reload, data } = useFetch<ApiResponse<Data[]>>(
    endpoints.verification.getDocumentList
  );
  const { stage } = useSelector(selectVerificationDetails);
  // const uiUpdate = useSelector(selectUIUpdate);
  const [, meta, helpers] = useField<DocumentFieldValue[]>("documentsUpload");
  const { values, isSubmitting, isValidating } = useFormikContext<FormValues>();
  const [loading, setLoading] = useState<boolean>(true);
  let disable = isSubmitting || isValidating;
  // useEffect(() => {
  //     if (
  //         uiUpdate?.pushType === NotificationIdentifier.RELOAD_BUSINESS_DOCUMENT_LIST ||
  //         uiUpdate?.pushType === NotificationIdentifier.BUSINESS_DOCUMENT_VERIFIED //Will docs be verified on upload?
  //     ) {
  //         reload();
  //         dispatch(completeUIUpdate());
  //     }
  // }, [uiUpdate, reload, dispatch]);

  const saveForm = async () => {
    try {
      const response = await instance.post(
        endpoints.verification.saveFormData,
        {
          ...formatValues(values),
          venueGroupVerificationStep: getEquivalentBackendStage(
            values.venueGroupVerificationStep
          ),
        }
      );
    } catch (error) {
      Toast.openToastMessage(
        "Error saving form, please try again.",
        ToastMessageReason.ERROR
      );
    }
  };

  useEffect(() => {
    if (data && data.details) {
      //if field not set intilise document array
      setLoading(true);
      if (
        Array.isArray(meta.value) &&
        meta.value.length === 1 &&
        meta.value[0].documentTypesID === null
      ) {
        let documentUploads: DocumentFieldValue[] = [];
        data.details.forEach(document => {
          documentUploads.push({
            documentTypesID: document.documentId,
            fileName: null,
            bAlwaysRequired: document.bAlwaysRequired,
          });
        });
        helpers.setValue([...documentUploads]);
      } else if (Array.isArray(meta.value)) {
        const newDocuments: DocumentFieldValue[] = data.details.map(
          newDocument => {
            if (
              newDocument.documentStatusString === "Expired" ||
              newDocument.documentStatusString === "Rejected"
            ) {
              //reset documents that have expired or been rejected
              return {
                documentTypesID: newDocument.documentId,
                fileName: null,
                bAlwaysRequired: newDocument.bAlwaysRequired,
              };
            }

            //replace with data from new documents list (in case of document list updates)
            const matchedDocument = meta.value.find(
              document => document.documentTypesID === newDocument.documentId
            );

            return matchedDocument
              ? {
                  ...matchedDocument,
                  bAlwaysRequired: newDocument.bAlwaysRequired,
                }
              : {
                  documentTypesID: newDocument.documentId,
                  fileName: null,
                  bAlwaysRequired: newDocument.bAlwaysRequired,
                };
          }
        );
        helpers.setValue(newDocuments);
        saveForm();
      }
    }
    setLoading(false);
    //eslint-disable-next-line
  }, [data]); // ignore the meta.value dependency to avoid looping

  return (
    <>
      <StageIndicator currentStage={BusinessVerificationStage.Documents} />
      <div className="Docs">
        <div className="DocsHeader">
          <BackButton
            currentStage={BusinessVerificationStage.Documents}
            disable={disable}
          />
          <h1>Document Upload</h1>
          <ErrorM name={`documentsUpload`} />
          <p>Please upload the following documents:</p>
        </div>
        {loading ? (
          <Spinner />
        ) : (
          <>
            <div className="CardsSection">
              {data &&
                data.details.map((document, index: number) => (
                  <ManageBusinessDocumentCard
                    cardData={document}
                    key={document.documentId}
                    reload={reload}
                    index={index}
                  ></ManageBusinessDocumentCard>
                ))}
            </div>
            <div className="DocsFooter">
              <ExitContinueButton disabled={disable} />
            </div>
          </>
        )}
      </div>
    </>
  );
};

function ManageBusinessDocumentCard({
  cardData,
  reload,
  index,
}: {
  cardData: Data;
  reload: () => void;
  index: number;
}) {
  const dispatch = useDispatch();
  const [, meta, helpers] = useField(
    cardData.bAlwaysRequired
      ? `documentsUpload.${index}.fileName`
      : `documentsUpload.${index}`
  );
  const { values } = useFormikContext<any>();
  const fieldName = `documentsUpload.${index}`;
  const bRequired = cardData.bAlwaysRequired;
  const handleUploadDocument = (documentId: number, documentName: string) => {
    dispatch(
      openModal({
        modalType: ModalTypes.UPLOAD_BUSINESS_DOCUMENT,
        data: {
          documentId,
          documentName,
          reloadTable: reload,
          bRequired,
          helpers,
          values,
          fieldName,
        },
      })
    );
  };

  return (
    <div className="CardContainer">
      <div className="DocumentCard">
        <div className="CardTitle">{cardData.name} </div>
        <div className="CardBody">
          <h3 className="CardStatus">
            {cardData.documentStatusString === "Checking"
              ? "Uploaded"
              : cardData.documentStatusString}
          </h3>
          <img
            className="CardImage"
            src={
              cardData.documentStatusString === "Approved" ||
              cardData.documentStatusString === "Checking"
                ? GreenTick
                : RedX
            }
            alt={cardData.documentStatusString}
          ></img>
          <div className="CardCaption">{cardData.documentNotes}</div>
          {cardData.bAlwaysRequired && (
            <>
              <div className="CardSpacer"></div>
              <div className="CardRequired">Required</div>
            </>
          )}
        </div>
        <div className="CardFooter">
          <Button
            type="button"
            disabled={
              !statusesToUploadFrom.includes(cardData.documentStatusString)
            }
            onClick={() => {
              helpers.setTouched(true);
              handleUploadDocument(cardData.documentId, cardData.name);
            }}
          >
            Upload
          </Button>
        </div>
      </div>
      <ErrorM name={`documentsUpload.${index}.fileName`} />
      <ErrorM name={`documentsUpload.${index}`} />
    </div>
  );
}

export default BusinessVerificationDocuments;
